import { URLS, prismicClient } from '@/data';
import { usePrismicStaleTime } from '@/data/prismicHooks';
import { EventDetails, League } from '@/feature/event-details-sbk/types';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import {
    GameTrackerConfigDocument,
    GameTrackerConfigDocumentDataGameTrackerFeatureFlagsItem,
} from '@/types/prismic.generated';
import { FloatingModalConfig } from '@/utils/floatingModal/FloatingModalProvider';
import { useQuery } from '@tanstack/react-query';

const betGeniusAutoSelectTeamStats = `
(function() {
    setTimeout(() => {
        const targetText = 'TEAM STATS';
        const buttons = Array.from(document.querySelectorAll('button'));
        const targetButton = buttons.find(button => button.textContent.trim() === targetText);

        if (targetButton && 
            !targetButton.disabled && 
            window.getComputedStyle(targetButton).display !== 'none' && 
            window.getComputedStyle(targetButton).visibility !== 'hidden') {
                targetButton.click();
        } 
}, 1000); // Delay to ensure the page is fully loaded
return true;
})();
`;

const toEmbedUrl = (url: string): string => {
    const twitchMatch = url.match(/twitch\.tv\/([^/?#]+)/);
    if (twitchMatch) {
        // channel: the Twitch channel name extracted from the stream URL
        // parent: required by Twitch embed but not enforced in a native WebView — no real domain exists in this context
        return `https://player.twitch.tv/?channel=${twitchMatch[1]}&parent=betr.app&autoplay=true`;
    }
    return url;
};

const getInjectedJavaScript = (eventDetails: EventDetails): string => {
    if (eventDetails.status?.toUpperCase() === 'FINISHED') {
        return betGeniusAutoSelectTeamStats;
    }
    return '';
};

const getGameTrackerEnabled = (config: GameTrackerConfigDocumentDataGameTrackerFeatureFlagsItem[], league?: League) => {
    const leagueId = league?.id;
    const sportId = league?.sport?.id;

    if (!leagueId && !sportId) {
        return false;
    }

    const configItem = config.find(item => {
        if (item.league_id) {
            return item.league_id === leagueId;
        }

        if (item.sport_id) {
            return item.sport_id === sportId;
        }

        return false;
    });

    return configItem?.game_tracker_enabled ?? false;
};

export const useGameTrackerLeagueEnabled = (league?: League) => {
    const gameTrackerConfigUid = useJurisdictionStore(
        store => store.jurisdictionSettings?.globalSettings?.gameTrackerConfigUid
    );
    const staleTime = usePrismicStaleTime();

    const { data: enabled } = useQuery({
        queryKey: ['game-tracker-config', gameTrackerConfigUid],
        queryFn: () => {
            if (!gameTrackerConfigUid) {
                return undefined;
            }
            return prismicClient.getByUID<GameTrackerConfigDocument>('game_tracker_config', gameTrackerConfigUid);
        },
        enabled: !!gameTrackerConfigUid,
        staleTime,
        select: data => {
            if (!data) {
                return false;
            }

            const flags = data.data.game_tracker_feature_flags;
            return getGameTrackerEnabled(flags, league);
        },
    });

    return enabled;
};

export const useGameTrackerConfig = (league?: League, eventDetails?: EventDetails): FloatingModalConfig | undefined => {
    const gameTrackerEnabled = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings?.game_tracker_enabled
    );
    const productName = useJurisdictionStore(
        store => store.jurisdictionSettings?.globalSettings?.keyValuePairs?.ApiKeys?.BET_GENIUS_PRODUCT_NAME ?? 'betr'
    );
    const leagueEnabled = useGameTrackerLeagueEnabled(league);

    if (!gameTrackerEnabled || !league || !eventDetails || !leagueEnabled) {
        return;
    }

    const contentProps = {
        minimizedContentScale: 0.6,
        maximizedContentScale: 0.9,
        minimizedContentHeight: 180,
        maximizedContentHeight: 280,
    };

    const betGeniusId = eventDetails?.suppliers?.find(supplier => supplier.feed === 'BETGENIUS')?.external_id;
    // If the betGeniusId is a negative number, it means the event id is invalid
    if (betGeniusId && Number(betGeniusId) > 0) {
        return {
            url: `${URLS.BET_GENIUS_GAME_TRACKER_URL}?productName=${productName}&fixtureId=${betGeniusId}`,
            ...contentProps,
            injectedJavaScript: getInjectedJavaScript(eventDetails),
        };
    }

    if (eventDetails?.stream_links?.length) {
        return {
            url: toEmbedUrl(eventDetails.stream_links[0]),
            ...contentProps,
        };
    }
};
